<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      tableColumns: [
        { key: 'name', label: 'Name' },
        { key: 'timing', label: 'Timing' },
        { key: 'working_days', label: 'Working Days' },
        { key: 'hours', label: 'Hours (F/H/S)' },
        { key: 'created_at', label: 'Created' },
        { key: 'actions', label: 'Actions', width: '80px' },
      ],
      shifts: [],
      search: '',
      pagination: {
        current_page: 1,
        per_page: 50,
        last_page: 1,
        total: 0,
        links: [],
      },
      dayLabels: {
        mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
        fri: 'Fri', sat: 'Sat', sun: 'Sun',
      },
    };
  },
  mounted() {
    this.fetchShifts();
  },
  methods: {
    async fetchShifts(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/shifts', {
          params: {
            page,
            search: this.search || undefined,
          },
        });
        this.shifts = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.fetchShifts(1);
    },

    parseDays(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    // "10:00:00" -> "10:00 AM"
    formatTime(time) {
      if (!time) return '-';
      const [h, m] = time.split(':');
      let hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${hour}:${m} ${ampm}`;
    },

    openAdd() {
      this.$refs.shiftModal.add();
      this.$openModal('hrm_shift_modal');
    },

    openEdit(shift) {
      this.$refs.shiftModal.edit(shift);
      this.$openModal('hrm_shift_modal');
    },

    confirmDelete(shift) {
      this.$refs.shiftModal.setDelete(shift);
      this.$openModal('hrm_shift_delete_modal');
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-flex align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Shifts</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="openAdd">
            <i class="ti ti-circle-plus me-2"></i>Add Shift
          </a>
        </div>
      </div>

      <!-- Main Card -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">

        <!-- Card Header -->
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Shift List
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>

          <!-- Search -->
          <div class="d-flex align-items-center gap-2">
            <div class="l-search-wrap">
              <input type="text" class="l-search-input" v-model="search" placeholder="Search shifts..." @keyup.enter="onSearch" />
              <span style="padding:0 10px; color:#94a3b8;"><i class="ti ti-search"></i></span>
            </div>
          </div>
        </div>

        <!-- Card Body — Table -->
        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">

          <app-table
            :columns="tableColumns"
            :rows="shifts"
            :pagination="pagination"
            :loading="loading"
            empty-text="No shifts found."
            empty-icon="ti ti-clock-hour-4"
            @page-change="fetchShifts"
          >
            <!-- Timing -->
            <template #timing="{ row }">
              <span style="font-size:12px; color:#1e293b;">
                {{ formatTime(row.start_time) }} <span style="color:#cbd5e1;">→</span> {{ formatTime(row.end_time) }}
              </span>
            </template>

            <!-- Working Days -->
            <template #working_days="{ row }">
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="day in parseDays(row.working_days)"
                  :key="day"
                  class="day-badge"
                >{{ dayLabels[day] || day }}</span>
                <span v-if="!parseDays(row.working_days).length" style="color:#94a3b8;">-</span>
              </div>
            </template>

            <!-- Hours (Full / Half / Short) -->
            <template #hours="{ row }">
              <span style="font-size:11.5px; color:#475569;">
                {{ row.full_day_hours ?? '-' }} / {{ row.half_day_hours ?? '-' }} / {{ row.short_day_hours ?? '-' }}
              </span>
            </template>

            <!-- Created -->
            <template #created_at="{ row }">
              <span style="font-size:11.5px; color:#94a3b8;">{{ $formatDate(row.created_at) }}</span>
            </template>

            <!-- Actions -->
            <template #actions="{ row }">
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openEdit(row)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="confirmDelete(row)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </app-table>

        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-shift-modal ref="shiftModal" @updated="fetchShifts" />
</template>

<style scoped>
.l-search-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.l-search-wrap:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, .1);
}

.l-search-input {
  border: none;
  outline: none;
  height: 34px;
  padding: 0 10px;
  font-size: 12.5px;
  color: #1e293b;
  width: 200px;
  background: transparent;
}

.day-badge {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 5px;
  background: #eef2ff;
  color: #4f46e5;
  line-height: 1.4;
}
</style>